import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
  
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/faq`;

const EMPTY_FORM = {
  heading: "",
  subheading: "",
  image: null,          // null means "no file selected"
  faqs: [{ question: "", answer: "" }],
};

/* ══════════════════════════════════════════════════════
   STYLES  (injected once into <head>)
══════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg:        #f4f6fb;
    --surface:   #ffffff;
    --surface2:  #f0f2f8;
    --border:    #e2e6f0;
    --border2:   #c8cfe0;
    --accent:    #4f6ef7;
    --accent-dim:#3a56d4;
    --accent-bg: #eef1fe;
    --text:      #1a1d2e;
    --text-muted:#7a80a0;
    --danger:    #e53935;
    --danger-bg: #fdecea;
    --success:   #2e7d52;
    --success-bg:#eaf5ee;
    --warn-bg:   #fff8e1;
    --warn-text: #7a5100;
    --radius:    10px;
    --shadow:    0 2px 16px rgba(79,110,247,0.08);
    --shadow-lg: 0 8px 32px rgba(79,110,247,0.13);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── FIX: use faq- prefix consistently ── */
  .faq-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    padding: 40px 24px 80px;
  }

  .faq-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 36px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--border);
  }
  .faq-header-left h1 {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: var(--accent);
    letter-spacing: 0.01em;
  }
  .faq-header-left p { color: var(--text-muted); font-size: 0.85rem; margin-top: 4px; }

  .btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 20px; border: none; border-radius: var(--radius);
    font-family: 'DM Sans', sans-serif; font-size: 0.875rem; font-weight: 500;
    cursor: pointer; transition: all 0.18s ease; letter-spacing: 0.02em;
  }
  .btn-gold { background: var(--accent); color: #fff; box-shadow: 0 2px 8px rgba(79,110,247,0.25); }
  .btn-gold:hover { background: var(--accent-dim); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(79,110,247,0.3); }
  .btn-outline { background: var(--surface); border: 1px solid var(--border2); color: var(--text-muted); }
  .btn-outline:hover { border-color: var(--accent); color: var(--accent); }
  .btn-warn { background: var(--warn-bg); color: var(--warn-text); border: 1px solid #f5d07a; }
  .btn-warn:hover { background: #fff0c0; }
  .btn-danger { background: var(--danger-bg); color: var(--danger); border: 1px solid #ffcdd2; }
  .btn-danger:hover { background: var(--danger); color: #fff; }
  .btn-sm { padding: 6px 13px; font-size: 0.8rem; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none !important; }

  .btn-ghost {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 9px 14px; border: 1.5px dashed var(--border2); border-radius: var(--radius);
    font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 500;
    color: var(--accent); background: var(--accent-bg); cursor: pointer;
    transition: all 0.15s; width: 100%; margin-top: 4px;
  }
  .btn-ghost:hover { background: #dde4fd; border-color: var(--accent); }

  .faq-table-wrap { overflow-x: auto; border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow); }
  table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
  thead { background: var(--surface2); }
  thead th {
    padding: 14px 16px; text-align: left; font-weight: 600;
    font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.08em;
    color: var(--text-muted); border-bottom: 1px solid var(--border); white-space: nowrap;
  }
  tbody tr.main-row { background: var(--surface); border-bottom: 1px solid var(--border); transition: background 0.15s; }
  tbody tr.main-row:hover { background: #f7f8ff; }
  tbody tr.expand-row td { padding: 0; background: #f9faff; border-bottom: 2px solid var(--accent-bg); }
  td { padding: 12px 16px; vertical-align: middle; color: var(--text); }

  /* ── Thumbnail ── */
  .td-img { cursor: pointer; }
  .td-img .thumb-wrap { position: relative; display: inline-block; }
  .td-img img {
    width: 60px; height: 60px; object-fit: cover;
    border-radius: 8px; border: 2px solid var(--border2);
    transition: transform 0.18s, box-shadow 0.18s;
    display: block;
  }
  .td-img .thumb-wrap:hover img { transform: scale(1.07); box-shadow: 0 4px 16px rgba(79,110,247,0.18); border-color: var(--accent); }
  .td-img .thumb-wrap .zoom-hint {
    position: absolute; inset: 0; border-radius: 8px;
    background: rgba(79,110,247,0.18); display: flex; align-items: center; justify-content: center;
    opacity: 0; transition: opacity 0.15s; font-size: 1.1rem;
  }
  .td-img .thumb-wrap:hover .zoom-hint { opacity: 1; }
  .td-img .no-img {
    width: 60px; height: 60px; border-radius: 8px;
    background: var(--surface2); border: 2px dashed var(--border2);
    display: flex; align-items: center; justify-content: center; font-size: 1.4rem;
  }

  .td-title { font-weight: 600; font-size: 0.9rem; }
  .td-sub { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-muted); font-size: 0.82rem; }
  .td-actions { display: flex; gap: 8px; white-space: nowrap; }
  .badge {
    display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 0.72rem; font-weight: 600;
    background: var(--accent-bg); color: var(--accent); border: 1px solid #c5d0fc; letter-spacing: 0.04em;
  }

  /* ── FAQ Expand toggle button ── */
  .btn-expand {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border: 1px solid var(--border2); border-radius: 6px;
    font-size: 0.75rem; font-weight: 500; color: var(--text-muted);
    background: var(--surface2); cursor: pointer; transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .btn-expand:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }
  .btn-expand.active { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }

  /* ── Expanded FAQ panel inside table ── */
  .faq-expand-inner {
    padding: 16px 20px 20px;
    display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 10px;
  }
  .faq-expand-item {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 8px; padding: 12px 14px;
  }
  .faq-expand-item .fq { font-size: 0.82rem; font-weight: 600; color: var(--text); margin-bottom: 5px; }
  .faq-expand-item .fa { font-size: 0.8rem; color: var(--text-muted); line-height: 1.5; }

  /* ── Image Lightbox ── */
  .lightbox-overlay {
    position: fixed; inset: 0; background: rgba(10,12,24,0.82);
    z-index: 300; display: flex; align-items: center; justify-content: center;
    animation: fadeIn 0.2s ease; backdrop-filter: blur(6px); cursor: zoom-out;
  }
  .lightbox-img {
    max-width: 88vw; max-height: 82vh; border-radius: 12px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.5); border: 2px solid rgba(255,255,255,0.12);
    animation: popIn 0.22s cubic-bezier(0.34,1.56,0.64,1);
    cursor: default;
  }
  .lightbox-close {
    position: fixed; top: 20px; right: 24px;
    background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
    color: #fff; border-radius: 50%; width: 38px; height: 38px;
    font-size: 1.1rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(4px); transition: background 0.15s;
  }
  .lightbox-close:hover { background: rgba(255,255,255,0.22); }
  .lightbox-caption {
    position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
    background: rgba(255,255,255,0.1); backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.15); border-radius: 99px;
    padding: 7px 20px; color: #fff; font-size: 0.82rem; font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
  }
  @keyframes popIn { from { transform: scale(0.88); opacity: 0; } to { transform: scale(1); opacity: 1; } }

  .faq-empty { text-align: center; padding: 56px 24px; color: var(--text-muted); }
  .faq-empty .empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .faq-empty p { font-size: 0.9rem; }

  .faq-panel-overlay {
    position: fixed; inset: 0; background: rgba(26,29,46,0.4);
    z-index: 100; animation: fadeIn 0.2s ease; backdrop-filter: blur(2px);
  }
  .faq-panel {
    position: fixed; top: 0; right: 0;
    width: min(560px, 100vw); height: 100vh;
    background: var(--surface); border-left: 1px solid var(--border);
    z-index: 101; display: flex; flex-direction: column;
    box-shadow: -8px 0 40px rgba(79,110,247,0.12);
    animation: slideIn 0.28s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }

  .faq-panel-header {
    padding: 22px 24px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0; background: var(--surface);
  }
  .faq-panel-header h2 { font-family: 'Playfair Display', serif; font-size: 1.2rem; color: var(--accent); }
  .faq-panel-header p  { font-size: 0.78rem; color: var(--text-muted); margin-top: 2px; }

  .close-btn {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid var(--border2); background: var(--surface2);
    color: var(--text-muted); font-size: 1.1rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all 0.15s;
  }
  .close-btn:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }

  .faq-panel-body {
    flex: 1; overflow-y: auto; padding: 20px;
    scrollbar-width: thin; scrollbar-color: var(--border2) transparent;
    background: var(--bg); display: flex; flex-direction: column; gap: 16px;
  }

  .form-section { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px 18px 10px; }
  .form-section-title {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.1em;
    color: var(--accent); font-weight: 700; margin-bottom: 16px;
    padding-bottom: 10px; border-bottom: 1px solid var(--border);
  }
  .form-group { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; flex: 1; }
  .form-group label { font-size: 0.8rem; font-weight: 600; color: var(--text); letter-spacing: 0.01em; }
  .required { color: var(--accent); margin-left: 2px; }
  .form-group input[type="text"],
  .form-group textarea {
    background: var(--surface); border: 1.5px solid var(--border);
    border-radius: var(--radius); color: var(--text);
    font-family: 'DM Sans', sans-serif; font-size: 0.875rem; padding: 9px 12px;
    transition: border-color 0.15s, box-shadow 0.15s; width: 100%; outline: none; resize: vertical;
  }
  .form-group input::placeholder, .form-group textarea::placeholder { color: var(--text-muted); opacity: 1; }
  .form-group input:focus, .form-group textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79,110,247,0.12); }
  .form-group textarea { min-height: 80px; }

  .faq-item-card {
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 14px; margin-bottom: 10px;
  }
  .faq-item-card:last-of-type { margin-bottom: 0; }
  .faq-item-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .faq-item-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent); }
  .faq-remove-btn {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 10px; border: 1px solid #ffcdd2; border-radius: 6px;
    font-size: 0.75rem; font-weight: 500; color: var(--danger);
    background: var(--danger-bg); cursor: pointer; transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .faq-remove-btn:hover { background: var(--danger); color: #fff; border-color: var(--danger); }
  .faq-remove-btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .faq-item-card .form-group { margin-bottom: 10px; }
  .faq-item-card .form-group:last-child { margin-bottom: 0; }

  .upload-zone {
    border: 2px dashed var(--border); border-radius: var(--radius);
    padding: 24px 20px; text-align: center; cursor: pointer;
    transition: all 0.18s; position: relative; background: var(--surface2);
  }
  .upload-zone:hover { border-color: var(--accent); background: var(--accent-bg); }
  .upload-zone input[type="file"] { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }
  .upload-zone .upload-icon { font-size: 2rem; margin-bottom: 6px; }
  .upload-zone p { font-size: 0.82rem; color: var(--text-muted); }
  .upload-zone .file-name { color: var(--accent); font-size: 0.82rem; margin-top: 6px; font-weight: 500; }

  .img-preview { position: relative; display: block; }
  .img-preview img { width: 100%; max-height: 160px; object-fit: cover; border-radius: var(--radius); border: 1px solid var(--border2); }
  .img-preview .remove-img {
    position: absolute; top: 6px; right: 6px;
    background: rgba(255,255,255,0.92); border: 1px solid var(--border2);
    color: var(--danger); border-radius: 50%; width: 26px; height: 26px;
    font-size: 0.75rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 1px 4px rgba(0,0,0,0.12);
  }

  .faq-panel-footer {
    padding: 16px 24px; border-top: 1px solid var(--border);
    display: flex; gap: 10px; flex-shrink: 0;
    background: var(--surface); box-shadow: 0 -4px 16px rgba(0,0,0,0.04);
  }

  .toast {
    position: fixed; bottom: 28px; right: 28px;
    padding: 12px 20px; border-radius: var(--radius);
    font-size: 0.875rem; font-weight: 500; z-index: 200;
    animation: toastIn 0.25s ease; max-width: 320px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1); font-family: 'DM Sans', sans-serif;
  }
  .toast-success { background: var(--success-bg); border: 1px solid #a8d5b5; color: var(--success); }
  .toast-error   { background: #fff0ef; border: 1px solid #ffcdd2; color: var(--danger); }
  @keyframes toastIn { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  .confirm-modal {
    position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
    z-index: 200; background: rgba(26,29,46,0.45); backdrop-filter: blur(4px); animation: fadeIn 0.18s ease;
  }
  .confirm-box {
    background: var(--surface); border: 1px solid var(--border); border-radius: 14px;
    padding: 28px 32px; max-width: 380px; text-align: center;
    box-shadow: var(--shadow-lg); font-family: 'DM Sans', sans-serif;
  }
  .confirm-box .icon { font-size: 2.5rem; margin-bottom: 14px; }
  .confirm-box h3 { font-family: 'Playfair Display', serif; margin-bottom: 8px; color: var(--text); }
  .confirm-box p  { color: var(--text-muted); font-size: 0.875rem; margin-bottom: 22px; }
  .confirm-box .actions { display: flex; gap: 10px; justify-content: center; }

  .spinner {
    display: inline-block; width: 18px; height: 18px;
    border: 2px solid rgba(79,110,247,0.2); border-top-color: var(--accent);
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-row td { text-align: center; padding: 40px; color: var(--text-muted); }
`;

/* ══════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════ */
export default function FaqAdmin() {
  const [data, setData]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showPanel, setShowPanel]         = useState(false);
  const [editId, setEditId]               = useState(null);
  const [saving, setSaving]               = useState(false);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [toast, setToast]                 = useState(null);
  const [imagePreview, setImagePreview]   = useState(null);
  const [removeImage, setRemoveImage]     = useState(false);
  const [formData, setFormData]           = useState(EMPTY_FORM);
  const [lightbox, setLightbox]           = useState(null);   // { src, caption }
  const [expandedRow, setExpandedRow]     = useState(null);   // item._id
  const fileInputRef = useRef();

  /* inject CSS once */
  useEffect(() => {
    const id = "faq-admin-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);

  /* ── Fetch ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setData(res.data.data || []);
    } catch {
      showToast("Failed to load FAQ sections", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  /* ── Panel helpers ── */
  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setImagePreview(null);
    setRemoveImage(false);
    setEditId(null);
    setShowPanel(true);
  };

  const openEdit = (item) => {
    setFormData({
      heading: item.heading || "",
      subheading: item.subheading || "",
      image: null,   // always null on edit open; file must be re-selected if changing
      faqs: item.faqs?.length
        ? item.faqs.map((f) => ({ question: f.question, answer: f.answer }))
        : [{ question: "", answer: "" }],
    });
    setImagePreview(item.image ? `${ENV_API_BASE_URL}/uploads/${item.image}` : null);
    setRemoveImage(false);
    setEditId(item._id);
    setShowPanel(true);
  };

  const closePanel = () => {
    setShowPanel(false);
    setEditId(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  /* ── Field change ── */
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((p) => ({ ...p, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setRemoveImage(false);
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  /* ── FAQ row helpers ── */
  const addFaqRow    = () => setFormData((p) => ({ ...p, faqs: [...p.faqs, { question: "", answer: "" }] }));
  const removeFaqRow = (i) => setFormData((p) => ({ ...p, faqs: p.faqs.filter((_, idx) => idx !== i) }));
  const updateFaqRow = (i, field, value) =>
    setFormData((p) => ({ ...p, faqs: p.faqs.map((f, idx) => idx === i ? { ...f, [field]: value } : f) }));

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.heading.trim()) { showToast("Heading is required", "error"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("heading", formData.heading.trim());
      fd.append("subheading", formData.subheading.trim());

      const validFaqs = formData.faqs.filter((f) => f.question.trim() || f.answer.trim());
      fd.append("faqs", JSON.stringify(validFaqs));

      // FIX: Only append image if it's an actual File object — never append null/undefined/{}
      if (formData.image instanceof File) {
        fd.append("image", formData.image);
      } else if (removeImage) {
        fd.append("removeImage", "true");
      }
      // If neither, send nothing — backend keeps existing image

      if (editId) {
        await axios.put(`${API}/${editId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("FAQ section updated ✓");
      } else {
        await axios.post(API, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("FAQ section created ✓");
      }
      fetchData();
      closePanel();
    } catch (err) {
      showToast(err?.response?.data?.message || "An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const confirmDelete = (item) => setDeleteTarget(item);
  const handleDelete  = async () => {
    try {
      await axios.delete(`${API}/${deleteTarget._id}`);
      showToast("Section deleted");
      fetchData();
    } catch {
      showToast("Delete failed", "error");
    } finally {
      setDeleteTarget(null);
    }
  };

  /* ══════════ RENDER ══════════ */
  return (
    // FIX: was "wwo-root" — now matches CSS class "faq-root"
    <div className="faq-root">

      {/* ── Header ── */}
      {/* FIX: was "wwo-header" / "wwo-header-left" */}
      <div className="faq-header">
        <div className="faq-header-left">
          <h1>FAQ Section</h1>
          <p>Manage FAQ sections displayed on the homepage</p>
        </div>
        <button className="btn btn-gold" onClick={openCreate}>
          <span>＋</span> Add Section
        </button>
      </div>

      {/* ── Table ── */}
      {/* FIX: was "wwo-table-wrap" */}
      <div className="faq-table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Heading</th>
              <th>Subheading</th>
              <th>FAQ Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="loading-row">
                <td colSpan="6"><div className="spinner" style={{ margin: "0 auto" }} /></td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="6">
                  {/* FIX: was "wwo-empty" */}
                  <div className="faq-empty">
                    <div className="empty-icon">🧘</div>
                    <p>No sections yet. Click <strong>Add Section</strong> to get started.</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, i) => {
                const imgSrc = item.image ? `${ENV_API_BASE_URL}/uploads/${item.image}` : null;
                const isExpanded = expandedRow === item._id;
                return (
                  <React.Fragment key={item._id}>
                    <tr className="main-row">
                      <td>{i + 1}</td>

                      {/* ── Image thumbnail with lightbox trigger ── */}
                      <td className="td-img">
                        {imgSrc ? (
                          <div
                            className="thumb-wrap"
                            onClick={() => setLightbox({ src: imgSrc, caption: item.heading })}
                            title="Click to enlarge"
                          >
                            <img src={imgSrc} alt={item.heading} />
                            <div className="zoom-hint">🔍</div>
                          </div>
                        ) : (
                          <div className="no-img" title="No image">🖼️</div>
                        )}
                      </td>

                      <td className="td-title">{item.heading}</td>
                      <td className="td-sub">{item.subheading || "—"}</td>

                      {/* ── FAQ count badge + expand toggle ── */}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="badge">{item.faqs?.length || 0} item{item.faqs?.length !== 1 ? "s" : ""}</span>
                          {item.faqs?.length > 0 && (
                            <button
                              className={`btn-expand ${isExpanded ? "active" : ""}`}
                              onClick={() => setExpandedRow(isExpanded ? null : item._id)}
                            >
                              {isExpanded ? "▲ Hide" : "▼ View"}
                            </button>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="td-actions">
                          <button className="btn btn-warn btn-sm" onClick={() => openEdit(item)}>✏️ Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => confirmDelete(item)}>🗑 Delete</button>
                        </div>
                      </td>
                    </tr>

                    {/* ── Expanded FAQ rows ── */}
                    {isExpanded && (
                      <tr className="expand-row">
                        <td colSpan="6">
                          <div className="faq-expand-inner">
                            {item.faqs.map((faq, fi) => (
                              <div className="faq-expand-item" key={fi}>
                                <div className="fq">Q{fi + 1}. {faq.question || <em style={{ color: "var(--text-muted)" }}>No question</em>}</div>
                                <div className="fa">{faq.answer || <em style={{ color: "var(--text-muted)" }}>No answer</em>}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Slide-in Panel ── */}
      {showPanel && (
        <>
          {/* FIX: was "wwo-panel-overlay" / "wwo-panel" / "wwo-panel-header" / "wwo-panel-body" */}
          <div className="faq-panel-overlay" onClick={closePanel} />
          <div className="faq-panel">
            <div className="faq-panel-header">
              <div>
                <h2>{editId ? "Edit Section" : "New FAQ Section"}</h2>
                <p>{editId ? "Update the FAQ section details" : "Fill in details for the new section"}</p>
              </div>
              <button className="close-btn" onClick={closePanel}>✕</button>
            </div>

            <form className="faq-panel-body" onSubmit={handleSubmit} id="faq-form">

              {/* ── Section 1: Basic Info ── */}
              <div className="form-section">
                <div className="form-section-title">Basic Info</div>

                <div className="form-group">
                  <label>Heading <span className="required">*</span></label>
                  <input
                    type="text" name="heading" value={formData.heading}
                    onChange={handleChange} required
                    placeholder="e.g. Frequently Asked Questions"
                  />
                </div>

                <div className="form-group">
                  <label>Subheading</label>
                  <input
                    type="text" name="subheading" value={formData.subheading}
                    onChange={handleChange}
                    placeholder="e.g. Our instructors are here to help you grow."
                  />
                </div>
              </div>

              {/* ── Section 2: FAQ Items ── */}
              <div className="form-section">
                <div className="form-section-title">FAQ Items</div>

                {formData.faqs.map((faq, i) => (
                  <div className="faq-item-card" key={i}>
                    <div className="faq-item-header">
                      <span className="faq-item-label">FAQ {i + 1}</span>
                      <button
                        type="button" className="faq-remove-btn"
                        onClick={() => removeFaqRow(i)}
                        disabled={formData.faqs.length <= 1}
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Question</label>
                      <input
                        type="text" placeholder="Enter the question"
                        value={faq.question}
                        onChange={(e) => updateFaqRow(i, "question", e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Answer</label>
                      <textarea
                        placeholder="Enter the answer"
                        value={faq.answer}
                        onChange={(e) => updateFaqRow(i, "answer", e.target.value)}
                        style={{ minHeight: 72 }}
                      />
                    </div>
                  </div>
                ))}

                <button type="button" className="btn-ghost" onClick={addFaqRow}>
                  ＋ Add FAQ Item
                </button>
              </div>

              {/* ── Section 3: Image ── */}
              <div className="form-section">
                <div className="form-section-title">Section Image</div>

                {imagePreview ? (
                  <div className="img-preview">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button" className="remove-img"
                      onClick={() => {
                        setImagePreview(null);
                        setRemoveImage(true);
                        setFormData((p) => ({ ...p, image: null }));
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >✕</button>
                  </div>
                ) : (
                  <div className="upload-zone">
                    <input
                      type="file" name="image" accept="image/*"
                      onChange={handleChange} ref={fileInputRef}
                    />
                    <div className="upload-icon">📷</div>
                    <p>Click or drag an image here</p>
                    <p style={{ fontSize: "0.75rem", marginTop: 4 }}>JPG, PNG, WEBP — max 20 MB</p>
                    {formData.image instanceof File && (
                      <p className="file-name">{formData.image.name}</p>
                    )}
                  </div>
                )}
              </div>

            </form>

            {/* FIX: was "wwo-panel-footer" */}
            <div className="faq-panel-footer">
              <button
                type="submit" form="faq-form" className="btn btn-gold" disabled={saving}
                style={{ flex: 1, justifyContent: "center" }}
              >
                {saving ? <><span className="spinner" /> Saving…</> : editId ? "Update Section" : "Create Section"}
              </button>
              <button className="btn btn-outline" onClick={closePanel} disabled={saving}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* ── Image Lightbox ── */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img
            className="lightbox-img"
            src={lightbox.src}
            alt={lightbox.caption}
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox.caption && (
            <div className="lightbox-caption">📌 {lightbox.caption}</div>
          )}
        </div>
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <div className="confirm-modal">
          <div className="confirm-box">
            <div className="icon">🗑️</div>
            <h3>Delete Section?</h3>
            <p><strong>"{deleteTarget.heading}"</strong> will be permanently removed along with its image.</p>
            <div className="actions">
              <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
              <button className="btn btn-outline" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}